/**
 * 库存管理 - 枚举定义
 * 
 * @module types/inventory/enums
 * @description 定义库存批次和出入库事务相关的枚举类型
 * 
 * 📋 对应后端文件
 * - `backend/app/Modules/Inventory/Models/InventoryBatch.php` - 质量状态和状态常量
 * - `backend/app/Modules/Inventory/Models/InventoryTransaction.php` - 事务类型、业务类型、审核状态常量
 * 
 * ⚠️ 注意事项
 * - 枚举值必须与后端常量完全一致（包括大小写）
 * - 所有枚举值都有中文注释说明用途
 * 
 * 🔄 更新时机
 * - 后端新增质量状态时
 * - 后端新增库存状态时
 * - 后端新增事务类型、业务类型、审核状态时
 * 
 * ✅ 验证方式
 * - 对照 Model 常量定义（QUALITY_*, STATUS_*, TYPE_*, BUSINESS_*, APPROVAL_*）
 * - 确保枚举值与后端完全匹配
 * 
 * @last-updated 2026-01-31
 */

/**
 * 库存批次质量状态
 */
export enum InventoryBatchQualityStatus {
  /** 合格 */
  QUALIFIED = 'QUALIFIED',
  /** 待检 */
  PENDING = 'PENDING',
  /** 不合格 */
  REJECTED = 'REJECTED',
  /** 隔离 */
  QUARANTINE = 'QUARANTINE',
}

/**
 * 库存批次状态
 */
export enum InventoryBatchStatus {
  /** 正常 */
  ACTIVE = 1,
  /** 冻结 */
  FROZEN = 0,
}

/**
 * 库存锁定来源节点
 *
 * @description 对应 InventoryLock::SOURCE_* 常量；标识锁定/冻结动作来自哪个业务节点，
 * 供员工针对性处理（如 NCR 去质量管理处置、INIT 去建账审核）
 */
export enum InventoryLockSource {
  /** 手动冻结（整批状态锁） */
  FREEZE = 'FREEZE',
  /** 质量隔离（NCR 不合格处置） */
  NCR = 'NCR',
  /** 采购退货待处理 */
  PURCHASE_RETURN = 'PURCHASE_RETURN',
  /** 库存初始化建账待审核 */
  INIT = 'INIT',
  /** 手动锁定 */
  MANUAL = 'MANUAL',
}

/**
 * 出入库事务类型
 * 
 * @description 对应 InventoryTransaction::TYPE_* 常量
 */
export enum InventoryTransactionType {
  /** 入库 */
  INBOUND = 'INBOUND',
  /** 出库 */
  OUTBOUND = 'OUTBOUND',
  /** 调拨 */
  TRANSFER = 'TRANSFER',
  /** 调整 */
  ADJUST = 'ADJUST',
  /** 盘点 */
  INVENTORY = 'INVENTORY',
}

/**
 * 出入库业务类型
 * 
 * @description 对应 InventoryTransaction::BUSINESS_* 常量
 * 
 * 入库类型：PURCHASE_IN, PRODUCTION_IN, RETURN_IN, OTHER_IN, BORROW_RETURN, INVENTORY_GAIN
 * 出库类型：MATERIAL_OUT, SALES_OUT, SCRAP_OUT, PURCHASE_RETURN, OTHER_OUT, BORROW_OUT, INVENTORY_LOSS
 * 其他类型：TRANSFER, ADJUST, INIT
 */
export enum InventoryBusinessType {
  // ========== 入库类型 ==========
  /** 采购入库 - 采购物料到货入库 */
  PURCHASE_IN = 'PURCHASE_IN',
  /** 生产入库 - 生产完工产品入库 */
  PRODUCTION_IN = 'PRODUCTION_IN',
  /** 退料入库 - 生产退回物料入库 */
  RETURN_IN = 'RETURN_IN',
  /** 其他入库 - 其他原因入库 */
  OTHER_IN = 'OTHER_IN',
  /** 借用归还 - 归还借用物料 */
  BORROW_RETURN = 'BORROW_RETURN',
  /** 盘盈 - 盘点发现多余库存 */
  INVENTORY_GAIN = 'INVENTORY_GAIN',

  // ========== 出库类型 ==========
  /** 生产领料 - 生产投料消耗 */
  MATERIAL_OUT = 'MATERIAL_OUT',
  /** 销售出库 - 销售发货出库 */
  SALES_OUT = 'SALES_OUT',
  /** 报废出库 - 不良品报废出库 */
  SCRAP_OUT = 'SCRAP_OUT',
  /** 采购退货 - 退还供应商物料 */
  PURCHASE_RETURN = 'PURCHASE_RETURN',
  /** 其他出库 - 其他原因出库 */
  OTHER_OUT = 'OTHER_OUT',
  /** 借用出库 - 临时借用物料 */
  BORROW_OUT = 'BORROW_OUT',
  /** 盘亏 - 盘点发现缺失库存 */
  INVENTORY_LOSS = 'INVENTORY_LOSS',

  // ========== 其他类型 ==========
  /** 调拨 - 仓库/库位间移动 */
  TRANSFER = 'TRANSFER',
  /** 调整 - 手工修正库存数据 */
  ADJUST = 'ADJUST',
  /** 初始化 - 系统上线期初库存 */
  INIT = 'INIT',
}

/**
 * 出入库审核状态
 * 
 * @description 对应 InventoryTransaction::APPROVAL_* 常量
 */
export enum InventoryApprovalStatus {
  /** 待审核 */
  PENDING = 'PENDING',
  /** 已审核 */
  APPROVED = 'APPROVED',
  /** 已拒绝 */
  REJECTED = 'REJECTED',
}

/**
 * ⚠️ MaterialType 已移至 types/material/enums.ts
 * 
 * Inventory 模块应该从 Material 模块导入物料类型
 * 
 * 请使用：
 * ```typescript
 * import { MaterialType } from '../material'
 * ```
 * 
 * @deprecated 请从 @bayes-mes/shared/types/material 导入
 */
// export enum MaterialType { ... } - 已移至 types/material/enums.ts

/**
 * 盘点单类型
 * 
 * @description 对应 StocktakingOrder::TYPE_* 常量
 */
export enum StocktakingType {
  /** 全盘 */
  FULL = 'FULL',
  /** 部分盘点 */
  PARTIAL = 'PARTIAL',
  /** 抽盘 */
  SPOT = 'SPOT',
}

/**
 * 盘点单状态
 * 
 * @description 对应 StocktakingOrder::STATUS_* 常量
 */
export enum StocktakingOrderStatus {
  /** 待盘点 */
  PENDING = 'PENDING',
  /** 盘点中 */
  IN_PROGRESS = 'IN_PROGRESS',
  /** 已完成 */
  COMPLETED = 'COMPLETED',
  /** 已取消 */
  CANCELLED = 'CANCELLED',
}

/**
 * 盘点明细状态
 * 
 * @description 对应 StocktakingItem::STATUS_* 常量
 */
export enum StocktakingItemStatus {
  /** 待盘点 */
  PENDING = 'PENDING',
  /** 已盘点 */
  COUNTED = 'COUNTED',
  /** 已确认 */
  CONFIRMED = 'CONFIRMED',
}

